import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { images } from '../shared/assets';
import { useOverlayManager } from '../../contexts/OverlayContext';
import UnifiedNavigation from '../Navigation/UnifiedNavigation';
import ResortNavigation from '../ResortNavigation/ResortNavigation';
import { ContentfulImage, ContentfulText } from '../Contentful';
import { useNavigation, useSiteSettings } from '../../hooks/useContentful';
import { useTranslation } from '../../i18n';
import './MenuOverlay.css';

const MenuOverlay: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { overlayState, toggleMenu, toggleResortNav } = useOverlayManager();
  const { t } = useTranslation();
  
  // Determine current page variant based on location
  const getCurrentPageVariant = (): string | null => {
    const path = location.pathname;
    if (path.includes('/atacama') || path === '/meetings-events' || path === '/getting-here' || path === '/experiences') return 'atacama';
    if (path.includes('/patagonia')) return 'patagonia';
    if (path.includes('/iguazu')) return 'iguazu';
    if (path.includes('/mendoza')) return 'mendoza';
    if (path.includes('/santa-catarina')) return 'santa-catarina';
    return null;
  };
  
  const currentPageVariant = getCurrentPageVariant();
  
  // Fetch content from Contentful
  const { data: navigationData } = useNavigation();
  const { data: siteSettingsData } = useSiteSettings();
  const navigation = navigationData?.[0]?.fields as any;
  const siteSettings = siteSettingsData?.[0]?.fields as any;
  
  const [collapsedSections, setCollapsedSections] = useState<{[key: string]: boolean}>(
    { artInitiatives: true, aboutUs: true }
  );
  
  // State for hover background image
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [previousHoveredItem, setPreviousHoveredItem] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get panel color based on hover state
  const getMenuItemColorTheme = (): string => {
    if (!hoveredItem) return 'default';
    
    const menuItemColorMap: { [key: string]: string } = {
      'ourResorts': 'patagonia',
      'artInitiatives': 'atacama', 
      'filmCollection': 'iguazu',
      'artImmersions': 'mendoza',
      'aboutUs': 'santa-catarina',
      'ourStory': 'atacama',
      'destinations': 'patagonia',
      'producingNature': 'iguazu',
      'reEnergize': 'mendoza',
      'gallery': 'santa-catarina'
    };
    
    return menuItemColorMap[hoveredItem] || 'default';
  };
  
  const colorVariant = getMenuItemColorTheme();
  
  // Atacama-specific background image from Figma
  const atacamaMenuBackground = '/assets/images/localhost/7af120af44c6f6e9de03adf9221594fd4c20cafa.png';
  
  // Image mapping for each menu item - using clean background photos from assets folder
  const menuBackgroundImages: { [key: string]: string } = {
    // Default state - clean background photo (same as resort navigation)
    default: '/assets/menu-default-bg.jpg',
    
    // Hover state images - clean background photos without UI elements
    ourResorts: '/assets/menu-our-resorts-hover-bg.jpg',
    artInitiatives: '/assets/menu-art-initiatives-hover-bg.jpg',
    aboutUs: '/assets/menu-about-us-hover-bg.jpg',
    producingNature: '/assets/menu-producing-nature-hover-bg.jpg',
    reEnergize: '/assets/menu-re-energize-hover-bg.jpg',
    gallery: '/assets/menu-gallery-hover-bg.jpg',
    
    // Sub-menu items with their hover states
    filmCollection: '/assets/menu-art-initiatives-hover-bg.jpg', // Same as art initiatives
    artImmersions: '/assets/menu-art-initiatives-hover-bg.jpg', // Same as art initiatives
    ourStory: '/assets/menu-about-us-hover-bg.jpg', // Same as about us
    destinations: '/assets/menu-about-us-hover-bg.jpg', // Same as about us
    
    // Other menu items - keep existing fallbacks
    packages: images.packageFullScreen || 'https://images.ctfassets.net/hz1mviiqoqof/xwfRb5N127YjFghLsnEM4/f562f5356027e441f3c6468308deaffb/packageFullScreen.png',
    blog: images.blogPage?.heroBackground || 'https://images.ctfassets.net/hz1mviiqoqof/4b06UMAjRTd4tMWwhF3tgY/0b7cad6af8a5834ddeafbe66370c1e07/gallery-hero-patagonia-landscape.png',
    
    // Property-specific menu items
    experiences: atacamaMenuBackground,
    accommodations: atacamaMenuBackground,
    dining: atacamaMenuBackground,
    gettingHere: atacamaMenuBackground
  };

  // Preload all menu background images with priority loading
  useEffect(() => {
    // Preload images when component mounts, not when menu opens
    const preloadImages = async () => {
      const imagesToPreload = Object.entries(menuBackgroundImages)
        .filter(([key, url]) => typeof url === 'string' && url.startsWith('/'))
        .map(([key, url]) => ({ key, url: url as string }));
      
      // Priority order: default first, then others
      const sortedImages = imagesToPreload.sort((a, b) => {
        if (a.key === 'default') return -1;
        if (b.key === 'default') return 1;
        return 0;
      });
      
      // Create link preload tags for critical images
      sortedImages.slice(0, 3).forEach(({ url }) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = url;
        // Use setAttribute for fetchPriority to avoid TypeScript error
        link.setAttribute('fetchpriority', 'high');
        document.head.appendChild(link);
      });
      
      // Preload all images with loading optimization
      const imagePromises = sortedImages.map(({ url }) => {
        return new Promise<void>((resolve) => {
          const img = new Image();
          img.decoding = 'async'; // Async decoding for performance
          img.loading = 'eager'; // Load immediately
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Don't block on errors
          img.src = url;
        });
      });
      
      // Load images in batches to prevent network congestion
      const batchSize = 3;
      for (let i = 0; i < imagePromises.length; i += batchSize) {
        await Promise.all(imagePromises.slice(i, i + batchSize));
      }
    };
    
    // Start preloading after a short delay to not block initial render
    const timeoutId = setTimeout(preloadImages, 100);
    
    return () => clearTimeout(timeoutId);
  }, []); // Run once on mount

  // Reset collapsed sections when menu is closed
  useEffect(() => {
    if (!overlayState.isMenuOpen) {
      setCollapsedSections({ artInitiatives: true, aboutUs: true });
      setHoveredItem(null); // Reset hover state when menu closes
      setPreviousHoveredItem(null);
      setIsTransitioning(false);
    }
  }, [overlayState.isMenuOpen]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
      if (hoverDebounceRef.current) {
        clearTimeout(hoverDebounceRef.current);
      }
    };
  }, []);

  // Debounce timer for hover events
  const hoverDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // Handle smooth hover transitions with debouncing
  const handleHoverChange = (newItem: string | null) => {
    // Clear any pending hover change
    if (hoverDebounceRef.current) {
      clearTimeout(hoverDebounceRef.current);
    }
    
    // Debounce hover changes by 50ms to prevent jitter
    hoverDebounceRef.current = setTimeout(() => {
      if (newItem === hoveredItem) return;
      
      // Performance monitoring in development
      if (process.env.NODE_ENV === 'development') {
        performance.mark('hover-transition-start');
      }
      
      // Clear any existing transition timeout
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
      
      // Use requestAnimationFrame for smoother transitions
      requestAnimationFrame(() => {
        setPreviousHoveredItem(hoveredItem);
        setHoveredItem(newItem);
        setIsTransitioning(true);
        
        // Reset transition state after animation completes
        transitionTimeoutRef.current = setTimeout(() => {
          setIsTransitioning(false);
          setPreviousHoveredItem(null);
          
          // Measure performance in development
          if (process.env.NODE_ENV === 'development') {
            performance.mark('hover-transition-end');
            performance.measure('hover-transition', 'hover-transition-start', 'hover-transition-end');
            const measure = performance.getEntriesByName('hover-transition')[0];
            if (measure && measure.duration > 450) {
              console.warn(`Slow hover transition detected: ${measure.duration.toFixed(2)}ms`);
            }
          }
        }, 400); // Match CSS transition timing (400ms)
      });
    }, 50); // 50ms debounce for smooth hover
  };
  
  // Get current background image based on hover state
  const getCurrentBackgroundImage = () => {
    if (hoveredItem && menuBackgroundImages[hoveredItem]) {
      console.log(`Hover item: ${hoveredItem}, Image: ${menuBackgroundImages[hoveredItem]}`);
      return menuBackgroundImages[hoveredItem];
    }
    return menuBackgroundImages.default;
  };

  const toggleSection = (sectionKey: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    toggleMenu(); // Close menu after navigation
  };

  return (
    <>
      {/* Full-Screen Menu Overlay */}
      <div 
        className={`menu-overlay ${overlayState.isMenuOpen ? 'open' : ''} ${overlayState.isResortNavOpen ? 'resort-nav-open' : ''}`}
        style={{
          backgroundColor: '#f3f3f3', // Always white background
          transition: 'background-color 0.3s ease'
        }}
      >
        <div className="menu-content">
          {/* Navigation Bar */}
          <UnifiedNavigation 
            variant="light" // Always use light variant (dark text on white background)
            destination={null} // No destination-specific styling
          />
          {/* Left Section - Main Navigation */}
          <div className="menu-main-nav">
            <button 
              onClick={toggleResortNav} 
              className="nav-primary transition-standard" 
              style={{'--stagger-delay': '0ms'} as React.CSSProperties}
              onMouseEnter={() => {
                handleHoverChange('ourResorts');
                setCollapsedSections({ artInitiatives: true, aboutUs: true });
              }}
              onMouseLeave={() => handleHoverChange(null)}
            >
              {t('navigation:ourResorts')}
            </button>
            
            <button 
              onClick={() => toggleSection('artInitiatives')} 
              className="nav-primary transition-standard" 
              style={{'--stagger-delay': '50ms'} as React.CSSProperties}
              onMouseEnter={() => {
                handleHoverChange('artInitiatives');
                setCollapsedSections(prev => ({ ...prev, aboutUs: true }));
              }}
              onMouseLeave={() => handleHoverChange(null)}
            >
              {t('navigation:artInitiatives')}
            </button>
            {!collapsedSections.artInitiatives && (
              <>
                <button 
                  onClick={() => handleNavigation('/film-collection')} 
                  className="nav-secondary transition-standard"
                  onMouseEnter={() => handleHoverChange('filmCollection')}
                  onMouseLeave={() => handleHoverChange(null)}
                >
                  {t('navigation:filmCollection')}
                </button>
                <button 
                  onClick={() => handleNavigation('/art-immersions')} 
                  className="nav-secondary transition-standard"
                  onMouseEnter={() => handleHoverChange('artImmersions')}
                  onMouseLeave={() => handleHoverChange(null)}
                >
                  {t('navigation:artImmersions')}
                </button>
              </>
            )}
            
            {/* Show About Us dropdown for brand level, direct About link for property level */}
            {currentPageVariant ? (
              <button 
                onClick={() => handleNavigation(`/${currentPageVariant}/about`)} 
                className="nav-primary transition-standard" 
                style={{'--stagger-delay': '100ms'} as React.CSSProperties}
                onMouseEnter={() => handleHoverChange('aboutUs')}
                onMouseLeave={() => handleHoverChange(null)}
              >
                <ContentfulText 
                  content={navigation?.menuItems?.about || "About"} 
                  fallback="About"
                />
              </button>
            ) : (
              <>
                <button 
                  onClick={() => toggleSection('aboutUs')} 
                  className="nav-primary transition-standard" 
                  style={{'--stagger-delay': '100ms'} as React.CSSProperties}
                  onMouseEnter={() => {
                    handleHoverChange('aboutUs');
                    setCollapsedSections(prev => ({ ...prev, artInitiatives: true }));
                  }}
                  onMouseLeave={() => handleHoverChange(null)}
                >
                  {t('navigation:aboutUs')}
                </button>
                {!collapsedSections.aboutUs && (
                  <>
                    <button 
                      onClick={() => handleNavigation('/our-story')} 
                      className="nav-secondary transition-standard"
                      onMouseEnter={() => handleHoverChange('ourStory')}
                      onMouseLeave={() => handleHoverChange(null)}
                    >
                      {t('navigation:ourStory')}
                    </button>
                    <button 
                      onClick={() => handleNavigation('/destinations')} 
                      className="nav-secondary transition-standard"
                      onMouseEnter={() => handleHoverChange('destinations')}
                      onMouseLeave={() => handleHoverChange(null)}
                    >
                      {t('navigation:destinations')}
                    </button>
                  </>
                )}
              </>
            )}
            
            <button 
              onClick={() => handleNavigation(currentPageVariant ? `/${currentPageVariant}/producing-nature` : '/producing-nature')} 
              className="nav-primary transition-standard" 
              style={{'--stagger-delay': '150ms'} as React.CSSProperties}
              onMouseEnter={() => {
                handleHoverChange('producingNature');
                setCollapsedSections({ artInitiatives: true, aboutUs: true });
              }}
              onMouseLeave={() => handleHoverChange(null)}
            >
              {t('navigation:producingNature')}
            </button>
            {/* Show different menu items based on current page */}
            {currentPageVariant ? (
              <>
                <button 
                  onClick={() => handleNavigation(`/${currentPageVariant}/experiences`)} 
                  className="nav-primary transition-standard" 
                  style={{'--stagger-delay': '200ms'} as React.CSSProperties}
                  onMouseEnter={() => {
                    handleHoverChange('experiences');
                    setCollapsedSections({ artInitiatives: true, aboutUs: true });
                  }}
                  onMouseLeave={() => handleHoverChange(null)}
                >
                  {t('navigation:experiences')}
                </button>
                <button 
                  onClick={() => handleNavigation(`/${currentPageVariant}/accommodations`)} 
                  className="nav-primary transition-standard" 
                  style={{'--stagger-delay': '220ms'} as React.CSSProperties}
                  onMouseEnter={() => {
                    handleHoverChange('accommodations');
                    setCollapsedSections({ artInitiatives: true, aboutUs: true });
                  }}
                  onMouseLeave={() => handleHoverChange(null)}
                >
                  {t('navigation:accommodations')}
                </button>
                {/* Show Dining for all properties */}
                <button 
                  onClick={() => handleNavigation(`/${currentPageVariant}/dining`)} 
                  className="nav-primary transition-standard" 
                  style={{'--stagger-delay': '240ms'} as React.CSSProperties}
                  onMouseEnter={() => {
                    handleHoverChange('dining');
                    setCollapsedSections({ artInitiatives: true, aboutUs: true });
                  }}
                  onMouseLeave={() => handleHoverChange(null)}
                >
                  {t('navigation:dining')}
                </button>
                <button 
                  onClick={() => handleNavigation(`/${currentPageVariant}/getting-here`)} 
                  className="nav-primary transition-standard" 
                  style={{'--stagger-delay': '260ms'} as React.CSSProperties}
                  onMouseEnter={() => {
                    handleHoverChange('gettingHere');
                    setCollapsedSections({ artInitiatives: true, aboutUs: true });
                  }}
                  onMouseLeave={() => handleHoverChange(null)}
                >
                  {t('navigation:gettingHere')}
                </button>
              </>
            ) : (
              <>
                <button 
                  onClick={() => handleNavigation('/re-energize')} 
                  className="nav-primary transition-standard" 
                  style={{'--stagger-delay': '200ms'} as React.CSSProperties}
                  onMouseEnter={() => {
                    handleHoverChange('reEnergize');
                    setCollapsedSections({ artInitiatives: true, aboutUs: true });
                  }}
                  onMouseLeave={() => handleHoverChange(null)}
                >
                  {t('navigation:reEnergize')}
                </button>
                <button 
                  onClick={() => handleNavigation('/gallery')} 
                  className="nav-primary transition-standard" 
                  style={{'--stagger-delay': '250ms'} as React.CSSProperties}
                  onMouseEnter={() => {
                    handleHoverChange('gallery');
                    setCollapsedSections({ artInitiatives: true, aboutUs: true });
                  }}
                  onMouseLeave={() => handleHoverChange(null)}
                >
                  {t('navigation:gallery')}
                </button>
              </>
            )}
          </div>
          
          {/* Secondary Navigation - Middle Right */}
          <div className="menu-secondary-nav">
            <button 
              onClick={() => handleNavigation('/packages')} 
              className="transition-standard"
            >
              {t('navigation:packages').toUpperCase()}
            </button>
            <button 
              onClick={() => handleNavigation('/blog')} 
              className="transition-standard"
            >
              {t('navigation:blog').toUpperCase()}
            </button>
            <button 
              onClick={() => handleNavigation('/contact')} 
              className="transition-standard"
            >
              {t('navigation:contact').toUpperCase()}
            </button>
            <button 
              onClick={() => handleNavigation('/press-awards')} 
              className="transition-standard"
            >
              {t('navigation:pressAwards').toUpperCase()}
            </button>
            {/* Additional items for property pages */}
            {currentPageVariant && (
              <>
                <button 
                  onClick={() => handleNavigation(`/${currentPageVariant}/gallery`)} 
                  className="transition-standard"
                >
                  <ContentfulText 
                    content={navigation?.secondaryItems?.gallery} 
                    fallback="GALLERY"
                  />
                </button>
                <button 
                  onClick={() => handleNavigation(`/${currentPageVariant}/meetings-events`)} 
                  className="transition-standard"
                >
                  <ContentfulText 
                    content={navigation?.secondaryItems?.meetingsEvents} 
                    fallback="MEETINGS & EVENTS"
                  />
                </button>
              </>
            )}
          </div>
          {/* Right Section - Background Image */}
          <div className="menu-background-image">
            {/* Base layer - always shows default */}
            <div 
              className="menu-background-layer menu-background-layer--base" 
              style={{ 
                backgroundImage: `url('${menuBackgroundImages.default}')`,
                opacity: hoveredItem ? 0 : 1
              }}
            />
            {/* Previous hover layer - for smooth transitions */}
            {previousHoveredItem && isTransitioning && (
              <div 
                className="menu-background-layer menu-background-layer--previous" 
                style={{ 
                  backgroundImage: `url('${menuBackgroundImages[previousHoveredItem] || menuBackgroundImages.default}')`,
                  opacity: isTransitioning ? 1 : 0
                }}
              />
            )}
            {/* Current hover layer */}
            <div 
              className="menu-background-layer menu-background-layer--current" 
              style={{ 
                backgroundImage: hoveredItem ? `url('${getCurrentBackgroundImage()}')` : 'none',
                opacity: hoveredItem ? 1 : 0,
                transitionDelay: '0s' // No delay for smoother transitions
              }}
            />
          </div>
        </div>
        
        
        {/* Contact Section - Direct child of menu-overlay */}
        <div className="menu-contact-section">
          <div className="menu-contact-info">
            <p>
              <ContentfulText 
                content={siteSettings?.contactEmail} 
                fallback="info@awasi.com"
              />
            </p>
            <p>
              <ContentfulText 
                content={siteSettings?.contactPhone} 
                fallback="+(56-2) 2233-9641"
              />
            </p>
          </div>
          <div className="social-links">
            <a href="https://www.instagram.com/awasiexperience/" className="social-link" target="_blank" rel="noopener noreferrer" aria-label={t('common:social.followOnInstagram')}>
              <ContentfulImage imageKey="icon-instagram" fallbackSrc={images.social.instagram} alt={t('common:social.instagram')} />
            </a>
            <a href="https://www.linkedin.com/company/awasi/" className="social-link" target="_blank" rel="noopener noreferrer" aria-label={t('common:social.followOnLinkedIn')}>
              <ContentfulImage imageKey="icon-linkedin" fallbackSrc={images.social.linkedIn} alt={t('common:social.linkedin')} />
            </a>
            <a href="https://open.spotify.com/user/awasiexperience" className="social-link" target="_blank" rel="noopener noreferrer" aria-label={t('common:social.followOnSpotify')}>
              <ContentfulImage imageKey="icon-spotify" fallbackSrc={images.social.spotify} alt={t('common:social.spotify')} />
            </a>
          </div>
        </div>
        
        {/* Relais & Châteaux Logo */}
        <div className="menu-relais-chateaux">
          <svg width="99" height="102" viewBox="0 0 99 102" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
            <path d="M43.8349 45.1852C40.23 46.2472 35.8173 48.0497 34.5644 52.0477C34.0341 54.2211 34.7278 56.3012 36.4602 57.6668C38.3341 59.167 41.2425 59.6823 43.3719 58.7041C44.7375 58.0789 45.7857 57.0183 46.2445 55.3697C44.8433 56.3685 42.6767 56.3245 41.3373 55.3546C40.2066 54.4533 39.5815 53.2058 39.6269 51.7743C39.8577 49.0224 42.7715 46.9671 45.1757 46.2719C45.9615 46.0397 46.8628 45.8543 47.7873 45.8103C47.2323 48.5141 44.9147 51.2962 42.424 52.6398C42.424 52.6398 44.3899 53.4573 47.4631 52.4639C47.4631 52.4639 47.8766 55.6266 49.495 58.5007C51.1133 55.628 51.5255 52.4639 51.5255 52.4639C54.6015 53.4573 56.566 52.6398 56.566 52.6398C54.0753 51.2962 51.7577 48.5141 51.2026 45.8103C52.1272 45.8543 53.0298 46.0397 53.8143 46.2719C56.2185 46.9685 59.1323 49.0238 59.3631 51.7743C59.4085 53.2058 58.7834 54.4533 57.6513 55.3546C56.3105 56.3245 54.1467 56.3685 52.7441 55.3697C53.2057 57.0183 54.2539 58.0789 55.6195 58.7041C57.7489 59.6823 60.6573 59.167 62.5311 57.6668C64.2621 56.3012 64.9559 54.2211 64.427 52.0477C63.1741 48.0497 58.7641 46.2472 55.1565 45.1852H55.1606C60.7754 44.6755 66.0934 43.1903 70.1833 39.1498C73.2345 35.9143 75.2019 31.338 73.4186 26.8757C72.3333 23.8243 69.2628 21.8253 66.1374 21.6536C58.6762 21.2456 55.2595 28.6329 54.8597 30.9684C54.8597 30.9684 58.8356 26.1049 64.4064 27.3881C66.1237 27.7824 67.797 29.2085 68.2655 31.0151C69.0485 34.322 67.7338 37.372 65.3063 39.6595C61.77 42.5955 57.426 43.7289 52.8705 44.1191C52.9872 41.6956 53.5848 39.474 55.1125 37.6701C56.2006 36.446 57.7626 35.597 59.2079 35.8951C60.5419 36.1699 61.057 37.4545 60.954 38.3227C60.7974 39.6416 59.5816 40.5209 59.5816 40.5209C62.0943 40.2709 64.6592 38.2046 65.3049 35.8223C66.0632 32.8465 63.545 29.541 59.4799 30.4793C53.5711 31.845 51.7865 38.6538 51.4623 44.2043C51.2603 44.2153 51.0625 44.2276 50.8688 44.2304C49.5527 37.8501 52.7551 32.6542 54.0725 27.0144C55.2183 22.0932 52.663 16.764 49.4964 12.8224C46.3297 16.764 43.7744 22.0932 44.9188 27.0144C46.2322 32.6542 49.44 37.8501 48.1225 44.2304C47.9302 44.2262 47.731 44.2153 47.5291 44.2043C47.2062 38.6538 45.4202 31.845 39.5115 30.4793C35.4491 29.541 32.9295 32.8451 33.6865 35.8223C34.3349 38.2032 36.8971 40.2709 39.4098 40.5209C39.4098 40.5209 38.1953 39.6416 38.0387 38.3227C37.9343 37.4558 38.4509 36.1699 39.7835 35.8951C41.2287 35.597 42.7921 36.446 43.8788 37.6701C45.4065 39.474 46.0041 41.6956 46.1209 44.1191C41.5681 43.7275 37.2241 42.5955 33.6851 39.6595C31.2576 37.372 29.9428 34.3207 30.7259 31.0151C31.1944 29.2099 32.8691 27.7838 34.5863 27.3881C40.1572 26.1049 44.1316 30.9684 44.1316 30.9684C43.7318 28.6329 40.3151 21.2456 32.8526 21.6536C29.7299 21.8253 26.6594 23.8257 25.5755 26.8757C23.7923 31.338 25.7609 35.913 28.8094 39.1498C32.9007 43.1903 38.2173 44.6755 43.8335 45.1852" fill="#49413D"/>
            <path d="M77.6323 74.3112L78.8275 76.0464H76.2969L75.9397 75.5202C75.1429 75.9324 74.2623 76.2182 73.2951 76.2182C71.4308 76.2182 69.9375 75.0517 69.9375 73.2451C69.9375 71.7077 70.792 70.8559 72.1562 70.1168C71.56 69.3488 71.2893 68.8089 71.2893 67.9695C71.2893 66.3208 72.4282 65.4951 74.0328 65.4951C75.5276 65.4951 76.5799 66.4898 76.5936 67.9695C76.6088 69.0067 75.8545 69.9464 75.044 70.5152L76.6225 72.8192C77.1336 72.3507 77.5897 71.852 77.9592 71.4247L79.1393 72.7478C78.7272 73.2451 78.2148 73.785 77.6309 74.3112M74.9588 74.0708L73.2388 71.5099C72.442 71.9372 72.1287 72.3493 72.1287 73.1311C72.1287 73.9128 72.7277 74.439 73.765 74.439C74.1331 74.439 74.5467 74.2975 74.9588 74.0708ZM73.2676 67.9282C73.2676 68.269 73.5658 68.7388 73.9504 69.2073C74.4629 68.824 74.831 68.3528 74.831 67.8994C74.831 67.3732 74.5329 67.0737 74.0493 67.0737C73.5658 67.0737 73.2676 67.3443 73.2676 67.9282Z" fill="#49413D"/>
            <path d="M25.5235 76.0478L22.6233 71.117V76.0478H20.3359V65.6146H24.081C26.2681 65.6146 27.6694 66.9308 27.6694 68.7306C27.6694 70.2885 26.7104 71.3175 25.3394 71.6899L28.1117 76.0491H25.5248L25.5235 76.0478ZM24.081 67.5435H22.6247V70.0591H24.081C24.8819 70.0591 25.3833 69.5439 25.3833 68.7306C25.3833 68.0024 24.8091 67.5449 24.081 67.5449" fill="#49413D"/>
            <path d="M37.2188 76.0477V65.6145H39.5048V74.1187H43.221V76.0477H37.2188Z" fill="#49413D"/>
            <path d="M51.2654 76.049L50.4659 73.418H47.291L46.4763 76.049H44.0625L47.7924 65.6158H49.9933L53.7823 76.049H51.2654ZM49.1223 68.9447C49.0082 68.5724 48.8928 68.0998 48.8928 68.0998C48.8928 68.0998 48.7912 68.5573 48.6771 68.9447L47.8913 71.4878H49.893L49.1209 68.9447H49.1223Z" fill="#49413D"/>
            <path d="M57.3563 65.6144H55.0703V76.0475H57.3563V65.6144Z" fill="#49413D"/>
            <path d="M62.205 76.2194C60.7611 76.2194 59.7033 75.6919 58.7031 74.9184L59.8887 73.3164C60.5042 73.8028 61.3889 74.2177 62.2476 74.2177C63.293 74.2177 63.5788 73.8591 63.5788 73.2299C63.5788 72.1871 61.6774 71.8299 60.8064 71.3436C59.761 70.772 58.9326 70.114 58.9326 68.4571C58.9326 66.3125 60.8064 65.4401 62.6487 65.4401C63.6351 65.4401 64.8221 65.7107 65.6656 66.3413L64.6366 68.0408C64.1805 67.6973 63.4071 67.4404 62.6501 67.4404C61.5936 67.4404 61.2227 68.0408 61.2227 68.4571C61.2227 69.3995 62.5512 69.6853 63.7656 70.2156C64.7795 70.658 65.8662 71.3724 65.8662 73.087C65.8662 75.0599 64.4649 76.218 62.2077 76.218" fill="#49413D"/>
            <path d="M18.1585 89.3495C14.9864 89.3495 12.8281 87.2489 12.8281 83.9035C12.8281 80.7724 15.187 78.5729 18.1173 78.5729C19.6464 78.5729 20.8031 79.0153 21.8747 80.1157L20.5874 81.616C19.8882 80.9167 19.0872 80.5732 18.1159 80.5732C16.3272 80.5732 15.1856 81.9883 15.1856 83.9049C15.1856 86.0481 16.2283 87.3505 18.1585 87.3505C19.4719 87.3505 20.1162 86.7914 20.788 86.1649L22.1041 87.6651C21.1315 88.7079 19.9747 89.3509 18.1599 89.3509" fill="#49413D"/>
            <path d="M39.9309 89.1777L39.1313 86.5481H35.9564L35.1417 89.1777H32.7266L36.4578 78.7445H38.6587L42.4477 89.1777H39.9309ZM37.7877 82.0734C37.6737 81.7038 37.5583 81.2285 37.5583 81.2285C37.5583 81.2285 37.4566 81.6873 37.3426 82.0734L36.5568 84.6192H38.5584L37.7863 82.0734H37.7877Z" fill="#49413D"/>
            <path d="M42.1562 78.7445V80.6734H44.228V89.1777H46.5167V80.6734H48.5321V78.7445H42.1562Z" fill="#49413D"/>
            <path d="M64.3371 89.1779L63.5376 86.5483H60.3627L59.548 89.1779H57.1328L60.8641 78.7448H63.065L66.8539 89.1779H64.3371ZM62.194 82.0736C62.0799 81.7041 61.9645 81.2287 61.9645 81.2287C61.9645 81.2287 61.8629 81.6876 61.7488 82.0736L60.963 84.6194H62.9647L62.1926 82.0736H62.194Z" fill="#49413D"/>
            <path d="M71.6703 89.3493C69.2703 89.3493 67.2109 87.8051 67.2109 84.946V78.743H69.5121V84.946C69.5121 86.3611 70.4119 87.375 71.6703 87.375C72.9288 87.375 73.9715 86.3611 73.9715 84.946V78.743H76.2424V84.946C76.2424 87.8037 74.0993 89.3493 71.6703 89.3493Z" fill="#49413D"/>
            <path d="M82.9999 78.7417L81.4997 81.4729L79.9129 78.7417H77.3824L80.2124 83.4857L76.8672 89.1762H79.4115L81.4997 85.4874L83.614 89.1762H86.1721L82.7554 83.4857L85.558 78.7417H82.9999Z" fill="#49413D"/>
            <path d="M49.6953 78.7417V89.1762H55.9833V87.2459H51.9827V84.9255H55.769V82.9924H51.9827V80.672H55.769V78.7417H49.6953Z" fill="#49413D"/>
            <path d="M31.5204 74.1186V71.7982H35.308V69.8624H31.5204V67.5447H35.308V65.6144H29.2344V76.0475H35.5223V74.1186H31.5204Z" fill="#49413D"/>
            <path d="M29.2465 78.7445V82.9898H25.6169V78.7445H23.3281V89.1777H25.6169V85.0052V84.9297H29.2465V85.0052V89.1777H31.5326V78.7445H29.2465Z" fill="#49413D"/>
          </svg>
        </div>
      </div>

      {/* Resort Navigation Overlay */}
      {overlayState.isResortNavOpen && <ResortNavigation onClose={toggleResortNav} />}
    </>
  );
};

export default MenuOverlay;